<template>
  <button type="button" :class="buttonClasses" :disabled="disabled || loading" v-bind="$attrs" @click="handleClick">
    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>

    <span :class="{ 'opacity-75': loading }" class="slot-wrapper">
      <slot v-if="$slots['prepend-icon'] && !loading" name="prepend-icon" />
      <component v-else-if="iconStart && !loading" :is="iconStart" :size="iconSize" />

      <slot />

      <slot v-if="$slots.icon && !loading" name="icon" />
      <component v-else-if="iconEnd && !loading" :is="iconEnd" :size="iconSize" />
    </span>
  </button>
</template>

<script setup lang="ts">
import type { Component } from 'vue';
import { computed } from 'vue';

export interface LeadButtonProps {
  /** `link` renders without button chrome */
  variant?:
    | 'primary'
    | 'secondary'
    | 'white'
    | 'danger'
    | 'success'
    | 'icon'
    | 'outline'
    | 'outline-danger'
    | 'secondary-outline'
    | 'link';
  loading?: boolean;
  disabled?: boolean;
  iconStart?: Component;
  iconEnd?: Component;
  iconSize?: number;
}

const props = withDefaults(defineProps<LeadButtonProps>(), {
  variant: 'primary',
  loading: false,
  disabled: false,
  iconStart: undefined,
  iconEnd: undefined,
  iconSize: 18,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClasses = computed(() => {
  const classes = ['btn', 'lead-button'];

  // Variant classes
  switch (props.variant) {
    case 'primary':
      classes.push('btn-primary');
      break;
    case 'secondary':
      classes.push('btn-secondary');
      break;
    case 'white':
      classes.push('btn-outline');
      break;
    case 'outline':
      classes.push('btn-outline-primary');
      break;
    case 'outline-danger':
      classes.push('btn-outline-danger');
      break;
    case 'secondary-outline':
      classes.push('btn-outline-secondary');
      break;
    case 'danger':
      classes.push('btn-danger');
      break;
    case 'success':
      classes.push('btn-success');
      break;
    case 'icon':
      classes.push('btn-icon');
      break;
    case 'link':
      classes.push('btn-link');
      break;
  }

  return classes;
});

const handleClick = (event: MouseEvent) => {
  if (!props.loading && !props.disabled) {
    emit('click', event);
  }
};
</script>

<style scoped>
* {
  --primary-color: var(--tblr-primary);
  --secondary-color: #000000;
  --success-color: #34c759;
  --danger-color: #f04438;
  font-family: var(--font-brand);
}

.lead-button {
  height: 40px;
  padding: 10px 14px;
  font-size: 13px;
  border-radius: 8px;
  font-weight: 600;
  position: relative;
  transition: all 0.2s ease-in-out;
  box-shadow: 0px 1px 2px 0px #1018280d;
}

.lead-button.btn-icon {
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
}

/* the base box-shadow would otherwise show as a faint border on the chromeless link */
.lead-button.btn-link {
  box-shadow: none;
}

.slot-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.lead-button.btn-primary {
  background-color: var(--primary-color);
  color: #ffffff;
}

.lead-button.btn-primary:hover {
  background-color: color-mix(in srgb, var(--primary-color) 80%, white 20%);
  box-shadow: 0px 2px 4px 0px #1018281a;
}

.lead-button.btn-secondary {
  background-color: var(--secondary-color);
  color: #ffffff;
}

.lead-button.btn-secondary:hover {
  background-color: color-mix(in srgb, var(--secondary-color) 80%, white 20%);
  box-shadow: 0px 2px 4px 0px #1018281a;
}

.lead-button.btn-outline {
  border: 1px solid #d0d5dd;
}

.lead-button.btn-outline-primary {
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
}

.lead-button.btn-outline-primary:hover {
  color: #ffffff;
  background-color: var(--primary-color);
}

.lead-button.btn-outline-danger {
  border: 2px solid var(--danger-color);
  color: var(--danger-color);
}

.lead-button.btn-outline-danger:hover {
  color: #ffffff;
  background-color: var(--danger-color);
}

/* secondary-outline: fills solid on hover */
.lead-button.btn-outline-secondary {
  border: 2px solid #000000;
  color: #000000;
}

.lead-button.btn-outline-secondary:hover,
.lead-button.btn-outline-secondary:active {
  color: #ffffff;
  background-color: #000000;
}
.btn-icon:hover,
.btn-outline:hover {
  background-color: #f9fafb;
  box-shadow: 0px 2px 4px 0px #1018281a;
}
/* Loading state */
.lead-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

/* Spinner animation */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.125em;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

.spinner-border {
  display: inline-block;
  vertical-align: -0.125em;
  border: 0.25em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}
</style>
